'use client';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../components/AuthProvider';
import { useSubscription } from '../components/SubscriptionProvider';
import { startCheckout, setPendingUpgrade, consumePendingUpgrade } from '../../lib/checkout';
import {
  PUBLIC_PLANS,
  PRICING,
  monthlyEquivalent,
  type BillingPeriod,
  type PublicPlanTier,
} from '../../lib/pricing';

function dollars(value: number): string {
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

export function PricingCards() {
  const { user, openSignIn } = useAuth();
  const { tier: currentTier, loading } = useSubscription();
  const posthog = usePostHog();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<PublicPlanTier | null>(null);
  const autoUpgradeHandled = useRef(false);
  const pricingRef = useRef<HTMLDivElement>(null);
  const viewTracked = useRef(false);

  useEffect(() => {
    const element = pricingRef.current;
    if (!element || loading || viewTracked.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || viewTracked.current) return;
      viewTracked.current = true;
      posthog?.capture('pricing_section_viewed', { current_tier: currentTier });
      observer.disconnect();
    }, { threshold: 0.35 });

    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, currentTier, posthog]);

  const beginCheckout = useCallback(async (
    targetTier: PublicPlanTier,
    period: BillingPeriod,
    trigger: 'pricing_page' | 'post_sign_in' = 'pricing_page',
  ) => {
    setLoadingPlan(targetTier);
    try {
      const url = await startCheckout(targetTier, period);
      posthog?.capture('checkout_started', {
        target_tier: targetTier,
        billing_period: period,
        current_tier: currentTier,
        trigger,
      });
      window.location.href = url;
    } catch (err) {
      console.error('[Pricing] Checkout error:', err);
      alert(err instanceof Error ? err.message : 'Checkout failed. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  }, [currentTier, posthog]);

  useEffect(() => {
    if (!user || loading || autoUpgradeHandled.current) return;
    const pending = consumePendingUpgrade();
    if (pending) {
      autoUpgradeHandled.current = true;
      const checkoutTimer = window.setTimeout(() => {
        beginCheckout(pending.tier, pending.billingPeriod, 'post_sign_in');
      }, 0);
      return () => window.clearTimeout(checkoutTimer);
    }
  }, [user, loading, beginCheckout]);

  const handleUpgrade = (targetTier: PublicPlanTier) => {
    posthog?.capture('checkout_clicked', {
      target_tier: targetTier,
      billing_period: billingPeriod,
      current_tier: currentTier,
      trigger: 'pricing_page',
    });
    if (!user) {
      setPendingUpgrade(targetTier, billingPeriod);
      openSignIn('/#pricing');
      return;
    }
    beginCheckout(targetTier, billingPeriod);
  };

  const isLegacy = currentTier !== 'free' && currentTier !== 'student';
  const isPaid = currentTier !== 'free';

  return (
    <div ref={pricingRef}>
      <div className="mb-9 flex flex-col items-center gap-3">
        <div className="inline-flex rounded-xl border border-border bg-muted-bg p-1" role="group" aria-label="Billing period">
          {(['monthly', 'annual'] as const).map((period) => (
            <button
              key={period}
              type="button"
              onClick={() => {
                setBillingPeriod(period);
                posthog?.capture('pricing_billing_period_changed', { billing_period: period });
              }}
              aria-pressed={billingPeriod === period}
              className={`ui-button ${
                billingPeriod === period
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted hover:text-foreground'
              }`}
            >
              {period === 'monthly' ? 'Monthly' : 'Yearly'}
            </button>
          ))}
        </div>
        {billingPeriod === 'annual' && (
          <p className="text-xs font-semibold text-primary">Save ${PRICING.unlimitedMonthly * 12 - PRICING.unlimitedAnnual} with yearly billing</p>
        )}
      </div>

      {isLegacy && (
        <div className="mb-6 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-center text-sm text-foreground/80">
          Your existing subscription is active.{' '}
          <Link href="/account" className="inline-flex min-h-10 items-center font-semibold text-primary hover:underline">Manage billing</Link>
        </div>
      )}

      <div className="grid gap-5 sm:grid-cols-2 sm:gap-6">
        {PUBLIC_PLANS.map((plan) => {
          const isCurrent = plan.tier === 'free' ? !isPaid : isPaid;
          const canCheckout = currentTier === 'free' && plan.tier !== 'free';
          const annual = billingPeriod === 'annual' && plan.annualPrice !== null;

          return (
            <div
              key={plan.tier}
              className={`card relative flex flex-col p-6 sm:p-7 ${
                plan.popular ? 'ring-2 ring-primary sm:-translate-y-2' : ''
              } ${isCurrent ? 'bg-primary/5' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-bold text-white">
                  Most popular
                </div>
              )}

              <div className="mb-5">
                <h3 className="font-serif text-lg font-semibold">{plan.name}</h3>
                <p className="mt-1 min-h-10 text-xs leading-relaxed text-muted">{plan.description}</p>
                <div className="mt-4">
                  {annual ? (
                    <>
                      <span className="text-3xl font-bold">{dollars(plan.annualPrice!)}</span>
                      <span className="ml-1 text-sm text-muted">/year</span>
                    </>
                  ) : (
                    <>
                      <span className="text-3xl font-bold">{dollars(plan.monthlyPrice)}</span>
                      <span className="ml-1 text-sm text-muted">{plan.tier === 'free' ? 'forever' : '/month'}</span>
                    </>
                  )}
                </div>
                {annual && (
                  <p className="mt-1 text-xs text-muted">{dollars(monthlyEquivalent(plan))}/mo, billed annually</p>
                )}
              </div>

              <ul className="mb-8 flex-1 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <svg className="mt-0.5 h-4 w-4 shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span className="text-foreground/80">{feature}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="ui-button w-full cursor-default bg-primary/10 text-primary">
                  Current plan
                </div>
              ) : canCheckout ? (
                <button
                  type="button"
                  onClick={() => handleUpgrade(plan.tier as PublicPlanTier)}
                  disabled={!!loadingPlan}
                  className={`ui-button w-full ${
                    plan.popular
                      ? 'ui-button--primary'
                      : 'ui-button--secondary'
                  }`}
                >
                  {loadingPlan === plan.tier ? (
                    <span className="mx-auto block h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </button>
              ) : isPaid && plan.tier !== 'free' ? (
                <Link
                  href="/account"
                  className="ui-button ui-button--secondary w-full text-center"
                >
                  Manage plan
                </Link>
              ) : (
                <div className="w-full py-2.5 text-center text-sm text-muted">Included</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

